import {DeliveryEstimates} from 'utilities/delivery-promise';

import {Scenario, ScenarioCost, ScenarioVariant} from './types';

function hasConditionallyFreeShipping(
  deliveryEstimates: DeliveryEstimates | null,
): boolean {
  return Number(deliveryEstimates?.freeDeliveryThreshold?.amount) > 0;
}

function isFreeShipping(deliveryEstimates: DeliveryEstimates | null): boolean {
  return (
    parseFloat(
      deliveryEstimates?.selectedShippingOption?.cost?.amount ?? '',
    ) === 0
  );
}

function canDisplayThisWeekendTitle(
  deliveryEstimates: DeliveryEstimates | null,
): boolean {
  const option = deliveryEstimates?.selectedShippingOption;
  if (!option?.maxDeliveryTime || !option?.maxCalendarDaysToDelivery)
    return false;

  const weekdayOfDelivery = new Date(option.maxDeliveryTime).getDay();
  const daysToArrive = option.maxCalendarDaysToDelivery;

  if (weekdayOfDelivery !== 4 && weekdayOfDelivery !== 5) {
    return false;
  }

  if (daysToArrive < 2 || daysToArrive > 4) {
    return false;
  }

  return weekdayOfDelivery - daysToArrive > 0;
}

export function expiresWithinFourHours(expiresAt: string) {
  const fourHours = 1000 * 60 * 60 * 4;

  return (
    new Date(expiresAt).getTime() - new Date(Date.now()).getTime() <= fourHours
  );
}

export function formatFreeShippingThreshold(freeShippingThreshold: number) {
  return freeShippingThreshold % 1 === 0
    ? freeShippingThreshold.toFixed(0)
    : freeShippingThreshold.toFixed(2);
}

export function getScenarioCost(
  deliveryEstimates: DeliveryEstimates | null,
): ScenarioCost {
  if (isFreeShipping(deliveryEstimates)) {
    return ScenarioCost.Free;
  }

  return hasConditionallyFreeShipping(deliveryEstimates)
    ? ScenarioCost.ConditionallyFree
    : ScenarioCost.Paid;
}

export function getScenarioVariant(
  deliveryEstimates: DeliveryEstimates | null,
): ScenarioVariant {
  const daysToDelivery =
    deliveryEstimates?.selectedShippingOption?.maxCalendarDaysToDelivery;

  switch (daysToDelivery) {
    case 0:
      return ScenarioVariant.SameDay;
    case 1:
      return ScenarioVariant.OneDay;
    case 2:
    case 3:
    case 4:
    case 5:
      if (canDisplayThisWeekendTitle(deliveryEstimates)) {
        return ScenarioVariant.BeforeTheWeekend;
      }
      return ScenarioVariant.ThreeOrMoreDays;
    default:
      return ScenarioVariant.ThreeOrMoreDays;
  }
}

export function getDefaultScenario() {
  return {
    title: '',
    locationPrefix: '',
    expiry: null,
    expiresAt: null,
    conditionallyFree: '',
    showLogo: false,
    holidayOverride: '',
    logoSrc: '',
    logoAltText: '',
    brandHandle: undefined,
    destinationAddress: undefined,
  } as Scenario;
}
